// Country-based city SUGGESTIONS (Sept 12, 2026 authorized owner feature).
// * Values are hints only — the input remains a free-text field so any
//   Arabic/English city name (including hyphens, apostrophes, spaces) is
//   accepted. `RegistrationFunnel` renders these via a <datalist>, which
//   is browser-native, keyboard-accessible, and mobile-touch-friendly.
// * When the applicant CHANGES country, the funnel clears any incompatible
//   city so a Riyadh suggestion never travels to a UAE application.
// * Owner directive: no restrictive lookup — a country not listed here
//   simply falls back to free-text with an empty suggestion pool.
// * Keys are the canonical English country name. Values are ordered by
//   likelihood of relevance to THE GATHERING attendees.
export const CITIES_BY_COUNTRY = {
  "Saudi Arabia": [
    { en: "Riyadh", ar: "الرياض" },
    { en: "Jeddah", ar: "جدة" },
    { en: "Dammam", ar: "الدمام" },
    { en: "Khobar", ar: "الخبر" },
    { en: "Mecca", ar: "مكة المكرمة" },
    { en: "Medina", ar: "المدينة المنورة" },
    { en: "Taif", ar: "الطائف" },
    { en: "Tabuk", ar: "تبوك" },
    { en: "Abha", ar: "أبها" },
    { en: "Buraidah", ar: "بريدة" },
    { en: "Hail", ar: "حائل" },
    { en: "Al Ahsa", ar: "الأحساء" },
    { en: "Yanbu", ar: "ينبع" },
    { en: "Jubail", ar: "الجبيل" },
    { en: "Najran", ar: "نجران" },
    { en: "Jazan", ar: "جازان" },
    { en: "Al Baha", ar: "الباحة" },
    { en: "Arar", ar: "عرعر" },
    { en: "Sakaka", ar: "سكاكا" },
    { en: "Qatif", ar: "القطيف" },
    { en: "Al Kharj", ar: "الخرج" },
  ],
  "United Arab Emirates": [
    { en: "Dubai", ar: "دبي" },
    { en: "Abu Dhabi", ar: "أبوظبي" },
    { en: "Sharjah", ar: "الشارقة" },
    { en: "Ajman", ar: "عجمان" },
    { en: "Ras Al Khaimah", ar: "رأس الخيمة" },
    { en: "Fujairah", ar: "الفجيرة" },
    { en: "Umm Al Quwain", ar: "أم القيوين" },
    { en: "Al Ain", ar: "العين" },
  ],
  "Kuwait": [
    { en: "Kuwait City", ar: "مدينة الكويت" },
    { en: "Hawalli", ar: "حولي" },
    { en: "Salmiya", ar: "السالمية" },
    { en: "Farwaniya", ar: "الفروانية" },
    { en: "Ahmadi", ar: "الأحمدي" },
    { en: "Jahra", ar: "الجهراء" },
  ],
  "Qatar": [
    { en: "Doha", ar: "الدوحة" },
    { en: "Al Rayyan", ar: "الريان" },
    { en: "Al Wakrah", ar: "الوكرة" },
    { en: "Al Khor", ar: "الخور" },
    { en: "Lusail", ar: "لوسيل" },
  ],
  "Bahrain": [
    { en: "Manama", ar: "المنامة" },
    { en: "Riffa", ar: "الرفاع" },
    { en: "Muharraq", ar: "المحرق" },
    { en: "Isa Town", ar: "مدينة عيسى" },
    { en: "Sitra", ar: "سترة" },
  ],
  "Oman": [
    { en: "Muscat", ar: "مسقط" },
    { en: "Salalah", ar: "صلالة" },
    { en: "Sohar", ar: "صحار" },
    { en: "Nizwa", ar: "نزوى" },
    { en: "Sur", ar: "صور" },
  ],
  "Jordan": [
    { en: "Amman", ar: "عمّان" },
    { en: "Zarqa", ar: "الزرقاء" },
    { en: "Irbid", ar: "إربد" },
    { en: "Aqaba", ar: "العقبة" },
  ],
  "Egypt": [
    { en: "Cairo", ar: "القاهرة" },
    { en: "Alexandria", ar: "الإسكندرية" },
    { en: "Giza", ar: "الجيزة" },
    { en: "Sharm El Sheikh", ar: "شرم الشيخ" },
  ],
  "United States": [
    { en: "Los Angeles", ar: "لوس أنجلوس" },
    { en: "New York", ar: "نيويورك" },
    { en: "Miami", ar: "ميامي" },
    { en: "Houston", ar: "هيوستن" },
    { en: "Chicago", ar: "شيكاغو" },
    { en: "Dallas", ar: "دالاس" },
  ],
  "United Kingdom": [
    { en: "London", ar: "لندن" },
    { en: "Manchester", ar: "مانشستر" },
    { en: "Birmingham", ar: "برمنغهام" },
  ],
  "Turkey": [
    { en: "Istanbul", ar: "إسطنبول" },
    { en: "Ankara", ar: "أنقرة" },
    { en: "Antalya", ar: "أنطاليا" },
  ],
};

// Return the suggestion pool for the given country (accepts EN or AR
// country label). Empty array means free-text only — never blocks entry.
export function citiesFor(country) {
  if (!country) return [];
  const c = String(country).trim();
  if (CITIES_BY_COUNTRY[c]) return CITIES_BY_COUNTRY[c];
  // AR label -> EN key fallback.
  for (const [enKey, list] of Object.entries(CITIES_BY_COUNTRY)) {
    if (list && Array.isArray(list)) {
      // no-op; the lookup is by EN key only, we handle AR via COUNTRIES elsewhere
    }
    if (enKey.toLowerCase() === c.toLowerCase()) return CITIES_BY_COUNTRY[enKey];
  }
  return [];
}
